import { ApolloClient, HttpLink, InMemoryCache, ApolloLink } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import AuthService from "@/core/services/Auth.service";
import { sessionRepository } from "@/infrastructure/repositories/session/Session.repository";
import { forceSessionLogout } from "@/infrastructure/services/auth/forceSessionLogout";
import { handleStudioUnauthorized } from "@/infrastructure/services/auth/handleStudioUnauthorized";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";
import { getClientTimezone } from "@/lib/utils/timezone";

let client: ApolloClient | null = null;

const SESSION_AUTH_HEADER = "X-Studio-Session-Authorization";
const SESSION_REFRESH_HEADER = "X-Studio-Session-Refresh-Token";

export function resolveFetchUri(uri: RequestInfo | URL): string {
  if (typeof uri === "string") {
    return uri;
  }
  if (uri instanceof URL) {
    return uri.href;
  }
  return uri.url;
}

async function buildAuthHeaders(
  existing?: HeadersInit,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (existing) {
    new Headers(existing).forEach((value, key) => {
      headers[key] = value;
    });
  }

  const auth0Token = await AuthService.getInstance().getUserAccessToken();
  const sessionAccessToken = sessionRepository.getSessionAccessToken();
  const sessionRefreshToken = sessionRepository.getSessionRefreshToken();

  if (auth0Token) {
    headers.Authorization = `Bearer ${auth0Token}`;
  }
  if (sessionAccessToken) {
    headers[SESSION_AUTH_HEADER] = `Bearer ${sessionAccessToken}`;
  }
  if (sessionRefreshToken) {
    headers[SESSION_REFRESH_HEADER] = `Bearer ${sessionRefreshToken}`;
  }

  return headers;
}

function applyRefreshedSessionToken(response: Response): void {
  const refreshedToken = response.headers.get(SESSION_AUTH_HEADER);
  if (refreshedToken) {
    sessionRepository.updateSessionAccessToken(refreshedToken);
  }
}

/** Clears the singleton so tests can re-initialize the client. */
export function resetApolloClientForTests(): void {
  client = null;
}

export function getApolloClient(): ApolloClient {
  if (!client) {
    const config = WebAppConfig.getInstance();

    const httpLink = new HttpLink({
      uri: config.graphqlBaseUrl,
      fetch: async (uri, options) => {
        const uriString = resolveFetchUri(uri);
        const url = new URL(uriString, window.location.origin);
        if (!url.searchParams.has("timezone")) {
          url.searchParams.set("timezone", getClientTimezone());
        }
        const requestUri = uriString.startsWith("http")
          ? url.toString()
          : `${url.pathname}${url.search}${url.hash}`;

        const response = await fetch(requestUri, options);
        applyRefreshedSessionToken(response);

        if (response.status !== 401 || !config.isAuthConfigured()) {
          return response;
        }

        const action = await handleStudioUnauthorized();
        if (action !== "retry") {
          return response;
        }

        try {
          const headers = await buildAuthHeaders(options?.headers);
          const retryResponse = await fetch(requestUri, { ...options, headers });
          applyRefreshedSessionToken(retryResponse);
          return retryResponse;
        } catch {
          // Retry failed for a non-auth reason; keep the original 401 response.
          return response;
        }
      },
    });

    const authLink = new SetContextLink(async (prevContext) => {
      if (!config.isAuthConfigured()) {
        return prevContext;
      }

      try {
        const auth0Token = await AuthService.getInstance().getUserAccessToken();
        const sessionAccessToken = sessionRepository.getSessionAccessToken();
        const sessionRefreshToken = sessionRepository.getSessionRefreshToken();

        return {
          ...prevContext,
          headers: {
            ...prevContext.headers,
            ...(auth0Token ? { Authorization: `Bearer ${auth0Token}` } : {}),
            ...(sessionAccessToken
              ? { [SESSION_AUTH_HEADER]: `Bearer ${sessionAccessToken}` }
              : {}),
            ...(sessionRefreshToken
              ? { [SESSION_REFRESH_HEADER]: `Bearer ${sessionRefreshToken}` }
              : {}),
          },
        };
      } catch {
        forceSessionLogout();
        return prevContext;
      }
    });

    client = new ApolloClient({
      link: ApolloLink.from([authLink, httpLink]),
      cache: new InMemoryCache(),
    });
  }
  return client;
}
